import type { ChangeEvent, KeyboardEvent } from 'react'
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'

import { PaperPlane } from '@oneToOne/shared/assets/PaperPlane'
import { ChatContext } from '@oneToOne/shared/components/chat/ChatContainer/ChatContext'
import { useChatTheme } from '@oneToOne/shared/components/chat/theme/useChatTheme'
import { UploadButton } from '@oneToOne/shared/components/chat/UploadButton'
import { ChatInputPopover } from '@oneToOne/shared/components/twind/ChatInput/ChatInputPopOver'
import type { DebouncedFunc } from 'lodash'

import { ControlsButton } from '@core/components/twind/ControlsButton'
import { useContextTranslation } from '@core/i18n'
import { useTwindContext } from '@core/twind'
import { isIOS, isMobileWeb } from '@core/userAgent'

import {
  chatButton,
  chatInputContainer,
  textAreaContainer,
  textAreaLabel,
} from './ChatInput.twind'

export interface ChatInputProps {
  placeholder?: string
  onEnter?: (message: string) => void
  onChange?: () => void
  handleFileChange?: (e: ChangeEvent<HTMLInputElement>) => void
  typingActivityHandler?: DebouncedFunc<() => void>
  backgroundColor?: string
}
/**
 * Chat Input Component, has a text area and a send button
 */
export const ChatInput: React.FC<ChatInputProps> = ({
  placeholder,
  onEnter,
  onChange,
  handleFileChange,
  typingActivityHandler,
}) => {
  const { t } = useContextTranslation()
  const { theme } = useChatTheme()
  const { tw } = useTwindContext()
  const { textSnippets } = useContext(ChatContext)

  const [value, setValue] = useState('')
  const [isComposing, setIsComposing] = useState(false)

  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const isMobileWebEnabled = isMobileWeb()

  function sendMessage() {
    const trimmedValue = value.trim()
    if (trimmedValue) {
      setValue('')
      onEnter?.(trimmedValue)
      typingActivityHandler?.cancel()
    }
  }

  function handleKeyUp(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === 'Enter' && !event.shiftKey && !isComposing) {
      event.preventDefault()
      sendMessage()
    }
  }

  useEffect(() => {
    // When the component mounts, focus on the textarea
    if (textareaRef.current && !isMobileWebEnabled) {
      textareaRef.current.focus()
    }
  }, [isMobileWebEnabled])

  const [dropdownStyle, setDropdownStyle] = useState({
    left: '0px',
    top: '0px',
  })

  const getCaretCoordinates = useCallback((textarea) => {
    const text = textarea.value
    const cursorPos = textarea.selectionStart
    const div = document.createElement('div')
    const style = getComputedStyle(textarea)
    const content = text.substring(0, cursorPos).replace(/\n/g, '<br>')

    div.style.position = 'absolute'
    div.style.top = textarea.offsetTop + 'px'
    div.style.left = textarea.offsetLeft + 'px'
    div.style.visibility = 'hidden'
    div.style.whiteSpace = 'pre-wrap'
    div.style.font = style.font
    div.style.width = style.width
    div.innerHTML = content + '<span style="position: relative;">|</span>'

    document.body.appendChild(div)
    const span = div.querySelector('span')
    let coordinates = { left: 0 }
    if (span) {
      coordinates = {
        left: span.offsetLeft + textarea.scrollLeft,
      }
    }

    document.body.removeChild(div)

    return coordinates
  }, [])

  // Reason for the label setting data-value to value: https://css-tricks.com/auto-growing-inputs-textareas/#aa-other-ideas
  return (
    <>
      <div
        className={tw?.(
          chatInputContainer({ theme }),
          'flex',
          'justify-center',
          'items-center',
          'h-auto',
          'w-full',
          'relative',
        )}
      >
        <ChatInputPopover
          value={value}
          setValue={setValue}
          dropdownStyle={dropdownStyle}
          textareaRef={textareaRef}
        />
        {handleFileChange ? (
          <UploadButton handleFileChange={handleFileChange} />
        ) : null}
        <div className={tw?.(textAreaContainer, 'w-full')}>
          <label
            className={`input-sizer ${tw?.(
              textAreaLabel({ theme, isMobileWeb: isMobileWebEnabled }),
              'inline-grid',
              'align-top',
              'relative',
              'rounded',
              'py-2',
              'pr-1',
              'pl-3',
              'items-stretch',
              'w-full',
              'overflow-y-auto',
              'overflow-x-hidden',
            )}`}
            data-value={value}
          >
            <textarea
              aria-label={t(`Your Message`)}
              ref={textareaRef}
              value={value}
              rows={1}
              placeholder={
                isMobileWeb() && isIOS
                  ? undefined
                  : placeholder
                    ? placeholder
                    : t('Send Message')
              }
              onInput={(e) => {
                if (textSnippets?.length) {
                  const coords = getCaretCoordinates(textareaRef.current)
                  setDropdownStyle({
                    ...dropdownStyle,
                    left: `${coords.left}px`,
                  })
                }

                setValue(e.currentTarget.value)
                onChange?.()
                typingActivityHandler?.()
              }}
              onKeyUp={handleKeyUp}
              onCompositionStart={() => setIsComposing(true)}
              onCompositionEnd={() => setIsComposing(false)}
            />
          </label>
        </div>
        <ControlsButton
          className={tw?.(
            chatButton({ theme, isMobileWeb: isMobileWebEnabled }),
            'flex',
            'justify-center',
            'items-center',
            'm-0',
            'rounded',
          )}
          onClick={sendMessage}
          imgSize={16}
          aria-label={t(`Send Message`)}
          src={
            <PaperPlane
              aria-hidden
              label=""
              width="16"
              height="16"
              fill={theme === 'light' ? 'var(--blk2)' : 'var(--cool-gray-900)'}
            />
          }
        />
      </div>
    </>
  )
}
